import { useMemo } from 'react'
import * as THREE from 'three'
import {
  COMPRIMENTO_PISTA,
  MEIA_LARGURA_PISTA,
  MEIA_LARGURA_TABULEIRO,
  Z_CENTRO_PISTA,
  Z_FIM_PISTA,
  Z_INICIO_PISTA,
} from './dimensoesMesa'

/**
 * Base de maquete do Table top: o tabuleiro em volta da pista.
 *
 * A pista sai pela borda do tabuleiro, como numa maquete física, e o corte da
 * base mostra as camadas. Calçada, meio-fio, grama, árvores, postes e casas
 * ficam na faixa externa, longe de onde a turma posiciona viaturas e vítimas.
 *
 * Nada aqui é clicável: o toque atravessa até o plano do chão de `CenaTabletop`,
 * que é quem posiciona peça nova e desfaz a seleção.
 */

const MEIA_PISTA = MEIA_LARGURA_PISTA
const MEIO_TABULEIRO = MEIA_LARGURA_TABULEIRO
/** Casas ao longo do lado −X, viradas para a pista: [z, largura, profundidade, altura, parede, telhado]. */
const CASAS = [
  [-29, 3.4, 6.0, 3.3, '#cdbfa6', '#7c4330'],
  [-18.5, 3.2, 5.2, 3.1, '#d8cbb5', '#8a4b35'],
  [-6.5, 3.2, 5.2, 3.1, '#e2d6c4', '#8a4b35'],
  [3.5, 3.2, 6.4, 3.6, '#b9c4cc', '#5a5f66'],
] as const
const LARGURA_CALCADA = 2.2
const ALTURA_CALCADA = 0.12
const ALTURA_GRAMA = 0.09
const ESPESSURA_BASE = 0.55
const TOPO_BASE = -0.012

const COR_BASE = '#3d2c22'
const COR_FRISO = '#c9b79c'
const COR_CONCRETO = '#a3a7ab'
const COR_JUNTA = '#7d8186'
const COR_MEIO_FIO = '#cfd2d5'
const COR_GRAMA = '#3e6a35'
const COR_TERRA = '#5a4433'

const semRaycast = () => null

/** Gerador determinístico: o tabuleiro é o mesmo em toda abertura. */
function sorteio(semente: number) {
  let s = semente >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 2 ** 32
  }
}

export function DioramaTabletop() {
  const xGrama = MEIA_PISTA + LARGURA_CALCADA
  const larguraGrama = MEIO_TABULEIRO - xGrama
  return (
    <group name="diorama-tabletop">
      <Base />
      {[-1, 1].map((lado) => (
        <group key={lado}>
          <Calcada lado={lado} />
          {/* grama sobre uma camada de terra: o corte da borda mostra as duas */}
          <mesh
            raycast={semRaycast}
            receiveShadow
            position={[lado * (xGrama + larguraGrama / 2), TOPO_BASE + ALTURA_GRAMA / 2, Z_CENTRO_PISTA]}
          >
            <boxGeometry args={[larguraGrama, ALTURA_GRAMA, COMPRIMENTO_PISTA]} />
            <meshStandardMaterial color={COR_TERRA} roughness={1} />
          </mesh>
          <mesh
            raycast={semRaycast}
            receiveShadow
            position={[lado * (xGrama + larguraGrama / 2), TOPO_BASE + ALTURA_GRAMA + 0.006, Z_CENTRO_PISTA]}
          >
            <boxGeometry args={[larguraGrama, 0.012, COMPRIMENTO_PISTA]} />
            <meshStandardMaterial color={COR_GRAMA} roughness={1} />
          </mesh>
          {/* postes só do lado das casas: do lado da câmera eles cortavam a cena */}
          {lado < 0 && [-30, -20, -10, 0, 10].map((z) => (
            <PosteDeLuz key={z} x={lado * (MEIA_PISTA + LARGURA_CALCADA - 0.45)} z={z} lado={lado} />
          ))}
        </group>
      ))}
      <Arvores />
      {CASAS.map(([z, largura, profundidade, altura, cor, telhado]) => (
        <Casa key={z} x={-10} z={z} largura={largura} profundidade={profundidade} altura={altura} cor={cor} telhado={telhado} />
      ))}
    </group>
  )
}

/** Tabuleiro de madeira escura, com um friso claro no topo. */
function Base() {
  const lado = MEIO_TABULEIRO * 2
  return (
    <group position={[0, 0, Z_CENTRO_PISTA]}>
      <mesh raycast={semRaycast} receiveShadow position={[0, TOPO_BASE - ESPESSURA_BASE / 2, 0]}>
        <boxGeometry args={[lado, ESPESSURA_BASE, COMPRIMENTO_PISTA]} />
        <meshStandardMaterial color={COR_BASE} roughness={0.55} metalness={0.05} />
      </mesh>
      <mesh raycast={semRaycast} position={[0, TOPO_BASE - 0.03, 0]}>
        <boxGeometry args={[lado + 0.02, 0.025, COMPRIMENTO_PISTA + 0.02]} />
        <meshStandardMaterial color={COR_FRISO} roughness={0.5} />
      </mesh>
    </group>
  )
}

/** Calçada de concreto com juntas de dilatação e meio-fio na borda da pista. */
function Calcada({ lado }: { lado: number }) {
  const xCentro = lado * (MEIA_PISTA + LARGURA_CALCADA / 2)
  const juntas = useMemo(() => {
    const lista: number[] = []
    for (let z = Z_INICIO_PISTA + 1.5; z < Z_FIM_PISTA; z += 1.5) lista.push(z)
    return lista
  }, [])
  const topo = TOPO_BASE + ALTURA_CALCADA
  return (
    <group>
      <mesh raycast={semRaycast} receiveShadow castShadow position={[xCentro, TOPO_BASE + ALTURA_CALCADA / 2, Z_CENTRO_PISTA]}>
        <boxGeometry args={[LARGURA_CALCADA, ALTURA_CALCADA, COMPRIMENTO_PISTA]} />
        <meshStandardMaterial color={COR_CONCRETO} roughness={0.92} />
      </mesh>
      {/* meio-fio: pedra mais clara na aresta que encara a pista */}
      <mesh
        raycast={semRaycast}
        receiveShadow
        castShadow
        position={[lado * (MEIA_PISTA + 0.08), TOPO_BASE + ALTURA_CALCADA / 2 + 0.004, Z_CENTRO_PISTA]}
      >
        <boxGeometry args={[0.16, ALTURA_CALCADA + 0.008, COMPRIMENTO_PISTA]} />
        <meshStandardMaterial color={COR_MEIO_FIO} roughness={0.8} />
      </mesh>
      {juntas.map((z) => (
        <mesh key={z} raycast={semRaycast} position={[xCentro + lado * 0.08, topo + 0.0015, z]}>
          <boxGeometry args={[LARGURA_CALCADA - 0.16, 0.002, 0.018]} />
          <meshStandardMaterial color={COR_JUNTA} roughness={1} />
        </mesh>
      ))}
    </group>
  )
}

function PosteDeLuz({ x, z, lado }: { x: number; z: number; lado: number }) {
  const base = TOPO_BASE + ALTURA_CALCADA
  const altura = 5.2
  return (
    <group position={[x, base, z]}>
      <mesh raycast={semRaycast} castShadow position={[0, altura / 2, 0]}>
        <cylinderGeometry args={[0.055, 0.08, altura, 10]} />
        <meshStandardMaterial color="#5b6168" metalness={0.6} roughness={0.45} />
      </mesh>
      {/* braço sobre a pista e a luminária */}
      <mesh raycast={semRaycast} castShadow position={[-lado * 0.6, altura - 0.05, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.035, 0.035, 1.2, 8]} />
        <meshStandardMaterial color="#5b6168" metalness={0.6} roughness={0.45} />
      </mesh>
      <mesh raycast={semRaycast} castShadow position={[-lado * 1.2, altura - 0.1, 0]}>
        <boxGeometry args={[0.5, 0.1, 0.22]} />
        <meshStandardMaterial color="#3f444a" metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh raycast={semRaycast} position={[-lado * 1.2, altura - 0.16, 0]}>
        <boxGeometry args={[0.42, 0.02, 0.16]} />
        <meshStandardMaterial color="#fff3d6" emissive="#ffe7b0" emissiveIntensity={0.6} />
      </mesh>
    </group>
  )
}

/** Árvores de maquete: tronco e copa facetada, em tons variados de verde. */
function Arvores() {
  const arvores = useMemo(() => {
    const aleatorio = sorteio(7)
    const lista: Array<{ x: number; z: number; altura: number; raio: number; cor: string; giro: number }> = []
    const verdes = ['#3b6b32', '#46783a', '#2f5a2b', '#557f3f']
    for (const lado of [1, -1]) {
      for (let z = Z_INICIO_PISTA + 1.2; z <= Z_FIM_PISTA - 1.2; z += 2.7) {
        // do lado das casas, só onde elas não estão
        if (lado < 0 && CASAS.some(([zc, , profundidade]) => Math.abs(z - zc) < profundidade / 2 + 1.2)) continue
        lista.push({
          x: lado * (9.6 + aleatorio() * 1.8),
          z: z + (aleatorio() - 0.5) * 1.1,
          altura: 1.5 + aleatorio() * 0.8,
          raio: 0.9 + aleatorio() * 0.45,
          cor: verdes[Math.floor(aleatorio() * verdes.length)]!,
          giro: aleatorio() * Math.PI,
        })
      }
    }
    return lista
  }, [])
  const base = TOPO_BASE + ALTURA_GRAMA
  return (
    <group>
      {arvores.map((a, i) => (
        <group key={i} position={[a.x, base, a.z]} rotation={[0, a.giro, 0]}>
          <mesh raycast={semRaycast} castShadow position={[0, a.altura / 2, 0]}>
            <cylinderGeometry args={[0.09, 0.14, a.altura, 7]} />
            <meshStandardMaterial color="#5a4030" roughness={0.95} />
          </mesh>
          <mesh raycast={semRaycast} castShadow position={[0, a.altura + a.raio * 0.7, 0]} scale={[1, 1.15, 1]}>
            <icosahedronGeometry args={[a.raio, 1]} />
            <meshStandardMaterial color={a.cor} roughness={0.9} flatShading />
          </mesh>
          <mesh raycast={semRaycast} castShadow position={[a.raio * 0.35, a.altura + a.raio * 1.25, -a.raio * 0.2]}>
            <icosahedronGeometry args={[a.raio * 0.62, 1]} />
            <meshStandardMaterial color={a.cor} roughness={0.9} flatShading />
          </mesh>
        </group>
      ))}
    </group>
  )
}

/** Casa térrea simples: paredes, telhado de duas águas, janelas e porta. */
function Casa({
  x,
  z,
  largura,
  profundidade,
  altura,
  cor,
  telhado,
}: {
  x: number
  z: number
  largura: number
  profundidade: number
  altura: number
  cor: string
  telhado: string
}) {
  const base = TOPO_BASE + ALTURA_GRAMA
  const geometriaTelhado = useMemo(() => {
    // prisma triangular ao longo de z: a cumeeira corre no comprimento da casa
    const forma = new THREE.Shape()
    const meia = largura / 2 + 0.25
    forma.moveTo(-meia, 0)
    forma.lineTo(meia, 0)
    forma.lineTo(0, 1.1)
    forma.lineTo(-meia, 0)
    const g = new THREE.ExtrudeGeometry(forma, { depth: profundidade + 0.4, bevelEnabled: false })
    g.translate(0, 0, -(profundidade + 0.4) / 2)
    return g
  }, [largura, profundidade])
  // a fachada encara a pista (+x)
  const janelas = [-profundidade / 4, profundidade / 4]
  return (
    <group position={[x, base, z]}>
      <mesh raycast={semRaycast} castShadow receiveShadow position={[0, altura / 2, 0]}>
        <boxGeometry args={[largura, altura, profundidade]} />
        <meshStandardMaterial color={cor} roughness={0.85} />
      </mesh>
      <mesh raycast={semRaycast} castShadow geometry={geometriaTelhado} position={[0, altura, 0]}>
        <meshStandardMaterial color={telhado} roughness={0.75} />
      </mesh>
      {janelas.map((zj) => (
        <mesh key={zj} raycast={semRaycast} position={[largura / 2 + 0.011, altura * 0.58, zj]}>
          <boxGeometry args={[0.02, 0.9, 1.1]} />
          <meshStandardMaterial color="#2a3a4a" metalness={0.3} roughness={0.2} />
        </mesh>
      ))}
      <mesh raycast={semRaycast} position={[largura / 2 + 0.011, 1.0, 0]}>
        <boxGeometry args={[0.02, 2.0, 0.9]} />
        <meshStandardMaterial color="#6b4a33" roughness={0.7} />
      </mesh>
    </group>
  )
}
